import { CoreQuery } from "@gen/app-query";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useConfig } from "~/contexts/config";
import { Money } from "~/entities/money";

function riskProfileInfo(risk?: string) {
  switch (risk) {
    case "not_assessed":
      return { label: "Not Assessed", color: "warning.main" };
    case "elevated":
      return { label: "Elevated", color: "warning.main" };
    case "normal":
      return { label: "Normal", color: "success.main" };
    case "highest":
      return { label: "Highest", color: "error.main" };
    default:
      return { label: "Unknown", color: "warning.main" };
  }
}

const declineCodeMessages: Record<string, string> = {
  authentication_required:
    "The card was declined as the transaction requires authentication.",
  approve_with_id: "The payment can’t be authorized.",
  call_issuer: "The card was declined for an unknown reason.",
  card_not_supported: "The card does not support this type of purchase.",
  card_velocity_exceeded:
    "The customer has exceeded the balance or credit limit available on their card.",
  currency_not_supported: "The card does not support the specified currency.",
  do_not_honor: "The card was declined for an unknown reason.",
  do_not_try_again: "The card was declined for an unknown reason.",
  duplicate_transaction:
    "A transaction with identical amount and credit card information was submitted very recently.",
  expired_card: "The card has expired.",
  fraudulent:
    "The payment was declined because Stripe suspects that it’s fraudulent.",
  generic_decline:
    "The card was declined for an unknown reason or possibly triggered by a blocked payment rule.",
  incorrect_number: "The card number is incorrect.",
  incorrect_cvc: "The CVC number is incorrect.",
  incorrect_pin:
    "The PIN entered is incorrect. This decline code only applies to payments made with a card reader.",
  incorrect_zip: "The postal code is incorrect.",
  insufficient_funds:
    "The card has insufficient funds to complete the purchase.",
  invalid_account: "The card, or account the card is connected to, is invalid.",
  invalid_amount:
    "The payment amount is invalid, or exceeds the amount that’s allowed.",
  invalid_cvc: "The CVC number is incorrect.",
  invalid_expiry_month: "The expiration month is invalid.",
  invalid_expiry_year: "The expiration year is invalid.",
  invalid_number: "The card number is incorrect.",
  invalid_pin:
    "The PIN entered is incorrect. This decline code only applies to payments made with a card reader.",
  issuer_not_available:
    "The card issuer couldn’t be reached, so the payment couldn’t be authorized.",
  lost_card: "The payment was declined because the card is reported lost.",
  merchant_blacklist:
    "The payment was declined because it matches a value on the Stripe user’s block list.",
  new_account_information_available:
    "The card, or account the card is connected to, is invalid.",
  no_action_taken: "The card was declined for an unknown reason.",
  not_permitted: "The payment isn’t permitted.",
  offline_pin_required: "The card was declined because it requires a PIN.",
  online_or_offline_pin_required: "The card was declined as it requires a PIN.",
  pickup_card:
    "The customer can’t use this card to make this payment (it’s possible it was reported lost or stolen).",
  pin_try_exceeded: "The allowable number of PIN tries was exceeded.",
  processing_error: "An error occurred while processing the card.",
  reenter_transaction:
    "The payment couldn’t be processed by the issuer for an unknown reason.",
  restricted_card:
    "The customer can’t use this card to make this payment (it’s possible it was reported lost or stolen).",
  revocation_of_all_authorizations:
    "The card was declined for an unknown reason.",
  revocation_of_authorization: "The card was declined for an unknown reason.",
  security_violation: "The card was declined for an unknown reason.",
  service_not_allowed: "The card was declined for an unknown reason.",
  stolen_card: "The payment was declined because the card is reported stolen.",
  stop_payment_order: "The card was declined for an unknown reason.",
  testmode_decline: "A Stripe test card number was used.",
  transaction_not_allowed: "The card was declined for an unknown reason.",
  try_again_later: "The card was declined for an unknown reason.",
  withdrawal_count_limit_exceeded:
    "The customer has exceeded the balance or credit limit available on their card.",
};

function declineCodeMessage(code?: string): string {
  if (!code) {
    return "";
  }
  return declineCodeMessages[code] ?? "Unknown decline code.";
}

interface StripePaymentStatusProps {
  paymentIntentId: string;
  stripePlatformId: string;
  isPaid: boolean;
  isCancelled: boolean;
  outcomeMessage?: string;
  isInReview?: boolean;
  isPaymentRejected?: boolean;
}

export function StripePaymentStatus({
  paymentIntentId,
  stripePlatformId,
  outcomeMessage,
  isPaid,
  isCancelled,
  isInReview,
  isPaymentRejected,
}: StripePaymentStatusProps) {
  const {
    stripe: { dashboardUrl: stripeUrl },
  } = useConfig();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { data: stripePaymentIntentData } =
    CoreQuery.useAdmin_getStripePaymentIntentQuery(
      stripePlatformId,
      paymentIntentId,
      {
        enabled: !!paymentIntentId && !!stripePlatformId,
      },
    );

  const stripePaymentIntent = stripePaymentIntentData;

  if (!stripePaymentIntent) {
    return null;
  }

  const pi = stripePaymentIntent;
  const charge = pi.charge;

  const fingerprint = charge?.payment_method?.fingerprint;

  const paymentAmountMoney = new Money(pi.amount, pi.currency);
  const feeMoney =
    charge?.fee !== undefined ? new Money(charge.fee, pi.currency) : null;
  const netMoney =
    charge?.net !== undefined ? new Money(charge.net, pi.currency) : null;

  let paymentStatusLabelSuffix = "Pending";
  if (isInReview) {
    paymentStatusLabelSuffix = "In Review";
  } else if (isPaymentRejected) {
    paymentStatusLabelSuffix = "Rejected";
  } else if (isPaid) {
    paymentStatusLabelSuffix = "Approved";
  } else if (isCancelled) {
    paymentStatusLabelSuffix = "Cancelled";
  }

  return (
    <Stack>
      <Accordion expanded={isExpanded} onChange={(_, ex) => setIsExpanded(ex)}>
        <AccordionSummary
          expandIcon={
            <PublicSvgIcon name={isExpanded ? "expand_less" : "expand_more"} />
          }
          data-testid="payment-status-button"
          sx={{
            backgroundColor: "blackStates._states.hover",
          }}
        >
          <Typography variant="body1" data-testid="payment-status-text">
            Payment ({paymentStatusLabelSuffix})
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: "blackStates._states.hover",
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={2}>
              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Payment
                </Typography>
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  spacing={1}
                  useFlexGap
                >
                  <Typography
                    variant="captionWithBackground"
                    data-testid="payment-intent-id-text"
                    sx={{
                      backgroundColor: "background.default",
                      width: "min-content",
                    }}
                  >
                    {paymentIntentId}
                  </Typography>
                  <Typography variant="body2" sx={{ marginTop: 0 }}>
                    created
                  </Typography>
                </Stack>
                <Typography variant="caption" align="center">
                  {pi?.created}
                </Typography>
              </Stack>
              {charge && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Charge
                  </Typography>
                  <Stack
                    flexDirection="row"
                    spacing={1}
                    useFlexGap
                    alignItems="center"
                  >
                    <Typography
                      variant="captionWithBackground"
                      data-testid="payment-charge-id-text"
                      sx={{
                        backgroundColor: "background.default",
                        width: "min-content",
                      }}
                    >
                      {charge.id}
                    </Typography>
                    <Typography variant="body2" sx={{ marginTop: 0 }}>
                      created
                    </Typography>
                  </Stack>
                  <Typography variant="caption" align="center">
                    {charge.created}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Typography
              variant="body1"
              data-testid="payment-metadata-heading-text"
            >
              Payment metadata
            </Typography>
            <Stack spacing={2}>
              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Description
                </Typography>
                <Typography
                  variant="captionWithBackground"
                  data-testid="payment-metadata-description-text"
                  sx={{
                    backgroundColor: "background.default",
                  }}
                >
                  {pi?.description}
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Transfer Group
                </Typography>
                <Typography
                  variant="caption"
                  data-testid="payment-metadata-tx_group-text"
                >
                  {charge?.tx_group ?? "N/A"}
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Statement Descriptor
                </Typography>
                <Typography
                  variant="caption"
                  data-testid="payment-metadata-statement_descr-text"
                >
                  {charge?.statement_descr ?? "N/A"}
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Attached metadata
                </Typography>
                <Typography
                  variant="captionWithBackground"
                  data-testid="payment-metadata-metadata-text"
                  sx={{
                    backgroundColor: "background.default",
                    whiteSpace: "pre-line",
                  }}
                >
                  {pi?.metadata}
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body1">Charge details</Typography>
            {fingerprint && (
              <Link
                href={`${stripeUrl ?? ""}/search?query=fingerprint:${fingerprint}`}
                target="_blank"
              >
                Fingerprint ID
              </Link>
            )}
            <Stack spacing={2} sx={{ mt: 2 }}>
              {charge?.risk && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Risk Profile
                  </Typography>
                  <Typography
                    variant="caption"
                    color={riskProfileInfo(charge.risk).color}
                  >
                    {`Risk: ${riskProfileInfo(charge.risk).label} ${charge?.risk_score ?? ""}`}
                  </Typography>
                </Stack>
              )}

              <Stack>
                <Typography variant="body2DashedDivider" fontWeight="bold">
                  Payment Amount
                </Typography>
                <Typography variant="caption" data-testid="payment-amount-text">
                  {paymentAmountMoney.format()}
                </Typography>
              </Stack>

              {feeMoney && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Fee
                  </Typography>
                  <Typography variant="caption" data-testid="payment-fee-text">
                    {feeMoney.format()}
                  </Typography>
                </Stack>
              )}

              {netMoney && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Net Amount
                  </Typography>
                  <Typography variant="caption" data-testid="payment-net-text">
                    {netMoney.format()}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.details?.last_4 && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Last 4
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.details.last_4}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.details?.expiry && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Expiry
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.details.expiry}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.type && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Type
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.type}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.details?.issuer && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Issuer
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.details.issuer}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.details?.country && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Country
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.details.country}
                  </Typography>
                </Stack>
              )}

              {charge?.payment_method.details?.cvc_check && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    CVC Check
                  </Typography>
                  <Typography variant="caption">
                    {charge.payment_method.details.cvc_check}
                  </Typography>
                </Stack>
              )}

              {charge?.address && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Address
                  </Typography>
                  <Typography
                    variant="captionWithBackground"
                    sx={{
                      backgroundColor: "background.default",
                    }}
                  >
                    {charge.address}
                  </Typography>
                </Stack>
              )}

              {charge?.network_status !== "approved_by_network" &&
                charge?.decline_code && (
                  <Stack>
                    <Typography variant="body2DashedDivider" fontWeight="bold">
                      Decline Reason
                    </Typography>
                    <Typography
                      variant="caption"
                      color={
                        charge.decline_code === "stolen_card"
                          ? "error.main"
                          : undefined
                      }
                    >
                      {declineCodeMessage(charge.decline_code)}
                    </Typography>
                  </Stack>
                )}
              {outcomeMessage && !isPaymentRejected && !isInReview && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Reason for approval: {outcomeMessage}
                  </Typography>
                </Stack>
              )}
              {isPaymentRejected && !isInReview && (
                <Stack>
                  <Typography variant="body2DashedDivider" fontWeight="bold">
                    Reason for rejection: {outcomeMessage}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
